import { Switch, Modal, Box, IconButton } from "@mui/material";
import React, { useState, useEffect } from "react";
import { Edit, Trash, ChevronDown, ChevronUp, X } from "lucide-react";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";
import ProductImageGallery from "./Components/ProductDetailsComponents/ProductImageGallery";
import ProductQuickStats from "./Components/ProductDetailsComponents/ProductQuickStats";
import ProductSpecifications from "./Components/ProductDetailsComponents/ProductSpecifications";
import ProductVariantsInventory from "./Components/ProductDetailsComponents/ProductVariantsInventory";
import ProductPackConfigurations from "./Components/ProductDetailsComponents/ProductPackConfigurations";
import ProductSellAs from "./Components/ProductDetailsComponents/ProductSellAs";
import { useNavigate, useParams } from "react-router-dom";
import { infoToast, successToast } from "../../../services/toast.service";
import {
  dummyProducts,
  transformCategories,
  dummyCategoryTree,
  productImages,
} from "../../../dummyData";

const ProductDetails = () => {
  const [activeTab, setActiveTab] = useState("specifications");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  // Get flattened categories for display
  const flattenedCategories = React.useMemo(() => {
    return transformCategories(dummyCategoryTree);
  }, []);

  // Helper function to get category display name
  const getCategoryDisplayName = categoryId => {
    const category = flattenedCategories.find(c => c.id === categoryId);
    return category ? category.name : "-";
  };

  useEffect(() => {
    // Simulate API call to fetch product data
    setLoading(true);
    console.log(id, "<<< product id");
    setTimeout(() => {
      const foundProduct = dummyProducts.find(product => product._id === id);

      if (foundProduct) {
        // Format the product data for display
        setProductData({
          id: foundProduct.productId,
          name: foundProduct.name,
          brand: foundProduct.brand.name,
          category: getCategoryDisplayName(foundProduct.primary_category),
          quantity: foundProduct.quantity,
          sku:
            foundProduct.productId +
            "-" +
            foundProduct.color.substring(0, 3).toUpperCase(),
          hsnCode: "6402", // This is static as it's not in the dummy data
          primaryUnit: foundProduct.soldAs || "Unit",
          inStock: foundProduct.inStock,
          color: foundProduct.color,
          mrp: foundProduct.mrp,
          sellingPrice: foundProduct.sellingPrice,
          taxRate: 12, // This is static as it's not in the dummy data
          material: foundProduct.material,
          size: foundProduct.size,
          description: foundProduct.description,
          sellAsOptions: foundProduct.sellAsOptions || null,
          // Use the dynamic product images if available, otherwise use an empty array
          images: productImages[foundProduct._id] || [],
          // Add any other data that might be needed by child components
        });
      } else {
        // Handle product not found - could redirect or show message
        infoToast("Product not found");
        navigate("/products");
      }
      setLoading(false);
    }, 800);
  }, [id, navigate, flattenedCategories]);

  const handleEditProduct = () => {
    // Close modal first
    setShowEditModal(false);

    // In a real implementation, you'd navigate to the edit product page with the product ID
    // For now we'll just show a mock implementation
    infoToast("Edit not available in demo.");
    // navigate(`/products/edit/${productData.id}`);
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 0,
  };

  if (loading || !productData) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-[#5652B0] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#F9FAFB]">
      {/* Header Section */}
      <BreadCrumbsContainer />
      <div className="bg-white border-b border-gray-200">
        <div className="flex flex-row items-center justify-between px-6 py-4">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold font-outfit text-gray-800">
              View Product
            </h1>
            <div className="flex space-x-2">
              <span className="px-3 py-1 bg-[#F9F8FE] text-[#5652B0] border border-[#EFEDFB] rounded-md text-sm font-medium">
                {productData.brand}
              </span>
              <span className="px-3 py-1 bg-[#F0FDF4] text-green-700 border border-[#DCFCE7] rounded-md text-sm font-medium">
                {productData.category}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Switch
                defaultChecked={productData.inStock}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#5652B0",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#5652B0",
                  },
                }}
              />
              <span className="text-sm font-medium text-gray-700">
                In Stock
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                className="p-2 text-[#5652B0] hover:bg-[#F9F8FE] border border-[#5652B0] rounded-md"
                onClick={() => setShowEditModal(true)}>
                <Edit size={18} />
              </button>
              <button
                className="p-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-md"
                onClick={() => setShowDeleteModal(true)}>
                <Trash size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 flex">
        {/* Left Sidebar */}
        <div className="w-1/3 border-r border-gray-200 bg-white overflow-y-auto">
          <div className="p-6">
            <ProductImageGallery images={productData.images} />
            <ProductQuickStats
              brand={productData.brand}
              mrp={productData.mrp}
              sp={productData.sellingPrice}
              quantity={productData.quantity || 0}
              inStock={productData.inStock}
            />
          </div>
        </div>

        {/* Right Content */}
        <div className="w-2/3 flex flex-col min-h-0">
          {/* Product Info Header */}
          <div className="bg-white p-6 border-b border-gray-200">
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-semibold font-outfit text-gray-800">
                    {productData.id}
                  </h2>
                  <p className="text-gray-600 mt-1">{productData.name}</p>
                </div>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 hover:bg-gray-50 rounded-md border border-gray-200 transition-colors">
                  {isExpanded ? (
                    <ChevronUp size={18} className="text-gray-600" />
                  ) : (
                    <ChevronDown size={18} className="text-gray-600" />
                  )}
                </button>
              </div>

              {isExpanded && (
                <div className="grid grid-cols-2 gap-6 pt-3 pb-1">
                  <div className="space-y-4">
                    <div className="bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Product Details
                      </p>
                      <div className="space-y-2">
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">SKU:</span>
                          <span className="font-medium text-gray-800">
                            {productData.sku}
                          </span>
                        </p>
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">HSN Code:</span>
                          <span className="font-medium text-gray-800">
                            {productData.hsnCode}
                          </span>
                        </p>
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">Primary Unit:</span>
                          <span className="font-medium text-gray-800">
                            {productData.primaryUnit}
                          </span>
                        </p>
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">Size:</span>
                          <span className="font-medium text-gray-800">
                            {productData.size || "-"}
                          </span>
                        </p>
                        <p className="text-sm flex justify-between items-center">
                          <span className="text-gray-600">Color:</span>
                          <span className="flex items-center gap-1.5">
                            <span className="w-4 h-4 bg-[#C4A484] rounded-full border border-gray-200"></span>
                            <span className="font-medium text-gray-800">
                              {productData.color}
                            </span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Pricing
                      </p>
                      <div className="space-y-2">
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">MRP:</span>
                          <span className="text-gray-400 line-through">
                            ₹{productData.mrp.toFixed(2)}
                          </span>
                        </p>
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">Selling Price:</span>
                          <span className="font-semibold text-[#5652B0]">
                            ₹{productData.sellingPrice.toFixed(2)}
                          </span>
                        </p>
                        <p className="text-sm flex justify-between">
                          <span className="text-gray-600">Tax Rate:</span>
                          <span className="font-medium text-gray-800">
                            {productData.taxRate}%
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white border-b border-gray-200 px-6">
            <div className="flex space-x-6">
              {["specifications", "variants", "packs", "sell_as"].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-3 px-2 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === tab
                      ? "border-[#5652B0] text-[#5652B0]"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  {tab === "sell_as"
                    ? "Sell As"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-[#F9FAFB]">
            {activeTab === "specifications" && (
              <ProductSpecifications
                material={productData.material}
                description={productData.description}
              />
            )}
            {activeTab === "variants" && (
              <ProductVariantsInventory productData={productData} />
            )}
            {activeTab === "packs" && (
              <ProductPackConfigurations productData={productData} />
            )}
            {activeTab === "sell_as" && (
              <ProductSellAs sellAsOptions={productData.sellAsOptions} />
            )}
          </div>
        </div>
      </div>

      {/* Edit Product Modal */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        aria-labelledby="edit-product-modal">
        <Box sx={modalStyle}>
          <div className="p-6 flex flex-col">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-semibold font-outfit text-gray-800">
                Edit Product
              </h2>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setShowEditModal(false)}
                aria-label="close">
                <X size={20} />
              </IconButton>
            </div>

            <div className="text-gray-600 mb-6">
              Choose what you would like to edit for {productData.id}:
            </div>

            <div className="space-y-3">
              <button
                onClick={handleEditProduct}
                className="w-full text-left p-3 flex items-center justify-between rounded-md hover:bg-[#F9F8FE] border border-gray-200">
                <span className="font-medium">Basic Information</span>
                <span className="text-sm text-gray-500">
                  Name, SKU, Brand, Category
                </span>
              </button>

              <button
                onClick={handleEditProduct}
                className="w-full text-left p-3 flex items-center justify-between rounded-md hover:bg-[#F9F8FE] border border-gray-200">
                <span className="font-medium">Specifications</span>
                <span className="text-sm text-gray-500">
                  Materials, Dimensions, Features
                </span>
              </button>

              <button
                onClick={handleEditProduct}
                className="w-full text-left p-3 flex items-center justify-between rounded-md hover:bg-[#F9F8FE] border border-gray-200">
                <span className="font-medium">Pricing & Inventory</span>
                <span className="text-sm text-gray-500">Price, Tax, Stock</span>
              </button>

              <button
                onClick={handleEditProduct}
                className="w-full text-left p-3 flex items-center justify-between rounded-md hover:bg-[#F9F8FE] border border-gray-200">
                <span className="font-medium">Variants</span>
                <span className="text-sm text-gray-500">
                  Sizes, Colors, Options
                </span>
              </button>

              <button
                onClick={handleEditProduct}
                className="w-full text-left p-3 flex items-center justify-between rounded-md hover:bg-[#F9F8FE] border border-gray-200">
                <span className="font-medium">Pack Configurations</span>
                <span className="text-sm text-gray-500">
                  Secondary and Tertiary Packs
                </span>
              </button>

              <button
                onClick={handleEditProduct}
                className="w-full text-left p-3 flex items-center justify-between rounded-md hover:bg-[#F9F8FE] border border-gray-200">
                <span className="font-medium">Sell As Configuration</span>
                <span className="text-sm text-gray-500">Selling Options</span>
              </button>
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  infoToast("Edit not available in demo.");
                  // setShowEditModal(false);
                  // navigate(`/products/edit/${productData.id}/all`);
                }}
                className="px-4 py-2 bg-[#5652B0] border border-[#5652B0] rounded-md text-white hover:bg-[#4A47A1]">
                Edit Full Product
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        aria-labelledby="delete-product-modal">
        <Box sx={modalStyle}>
          <div className="p-6 flex flex-col items-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Trash size={24} className="text-red-600" />
            </div>

            <h2 className="text-xl font-semibold font-outfit text-gray-800 mb-2">
              Delete Product
            </h2>

            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium">{productData.name}</span> (
              {productData.id})? This action cannot be undone.
            </p>

            <div className="flex space-x-4 w-full">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  infoToast("Delete not available in demo.");
                  // Handle delete logic here
                }}
                className="flex-1 px-4 py-2 bg-red-600 border border-red-600 rounded-md text-white hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default ProductDetails;
